import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { usePermissions } from '@/hooks/usePermissions';
import { sidebarFooterItems, visibleSections } from '@/lib/navigation';
import { cn } from '@/lib/cn';

/**
 * The shortcut as this platform writes it. The listener accepts both modifiers
 * everywhere; only the hint changes. "⌘K" on the Windows machines most staff use reads
 * as a Mac-only app.
 */
const SHORTCUT_LABEL =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
    ? '⌘K'
    : 'Ctrl K';

/**
 * Command palette (⌘K / Ctrl+K).
 *
 * It indexes ONLY what the current user may reach — the nav tree already filtered by
 * permission. A palette that searches a client-side store will happily surface records
 * the user has no right to see, which is both a leak and a support ticket.
 *
 * Record search (employees, tasks …) is added in the phase that owns those records, and
 * will query the API so the server does the scoping.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { can, role } = usePermissions();

  const sections = useMemo(() => {
    const main = visibleSections(can, role);
    const footer = sidebarFooterItems(can, role);
    return footer.length > 0
      ? [...main, { id: 'general', label: 'General', items: footer }]
      : main;
  }, [can, role]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // No aria-label here, deliberately. The name comes from the text below, which
        // keeps it IDENTICAL to what is on screen — an aria-label that omits the visible
        // "⌘K" trips axe's label-content-name-mismatch and, more importantly, breaks
        // speech input for anyone saying what they can see.
        // A circular icon below `md`, where the header has no room, and a wide pill
        // above it. The pill is the reference kit's search field: it reads as somewhere
        // you type rather than as a button that happens to mention searching.
        className={cn(
          'flex shrink-0 items-center gap-2 rounded-full bg-subtle text-fg-muted',
          'transition-colors hover:bg-hover',
          'size-9 justify-center',
          'md:h-9 md:w-full md:max-w-sm md:justify-start md:px-4',
        )}
      >
        <Search aria-hidden className="size-4 shrink-0" />
        {/* sr-only below `md`: the words stay in the accessible name at every width,
            so the control is never nameless on mobile, while `hidden` keeps the
            shortcut out of both the name and the screen when there is no room. */}
        <span className="sr-only text-body md:not-sr-only">Search anything</span>
        <kbd className="ml-auto hidden rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[10px] md:inline">
          {SHORTCUT_LABEL}
        </kbd>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="gap-0 p-0 sm:max-w-xl" hideClose>
          <DialogTitle className="sr-only">Command palette</DialogTitle>
          <Command loop className="overflow-hidden">
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search aria-hidden className="size-4 shrink-0 text-fg-subtle" />
              <Command.Input
                placeholder="Search pages and actions…"
                className="h-12 w-full bg-transparent text-body-lg outline-none placeholder:text-fg-subtle"
              />
            </div>

            <Command.List className="max-h-80 overflow-y-auto p-2">
              <Command.Empty className="py-8 text-center text-caption text-fg-muted">
                Nothing matches that search.
              </Command.Empty>

              {sections.map((section) => (
                  <Command.Group
                    key={section.id}
                    heading={section.label}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-fg-muted"
                  >
                    {section.items.map((item) => (
                      <Command.Item
                        key={item.to}
                        value={`${section.label} ${item.label}`}
                        onSelect={() => go(item.to)}
                        className={cn(
                          'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-2 text-body',
                          'data-[selected=true]:bg-hover',
                        )}
                      >
                        <item.icon aria-hidden className="size-4 text-fg-muted" />
                        {item.label}
                      </Command.Item>
                    ))}
                  </Command.Group>
              ))}
            </Command.List>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}
